import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { HiSparkles, HiX, HiDownload } from './Icons';
import './PWAInstallBanner.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);

  useEffect(() => {
    // Check if user previously dismissed in last 7 days
    const dismissedTime = localStorage.getItem('pwa_banner_dismissed_time');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime, 10)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Detect standalone PWA mode (already installed)
    const isStandalone =
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.navigator as any)?.standalone === true;

    if (isStandalone) {
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Listen for BeforeInstallPrompt event (Android / Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // If iOS and not in standalone, show after a delay
    if (isIosDevice) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 5000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed_time', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <aside className="pwa-banner-wrapper" aria-label="App Installation">
      <div className="pwa-banner-card">
        <div className="pwa-banner-left">
          <div className="pwa-app-icon">
            <HiSparkles className="pwa-svg-icon" />
          </div>
          <div className="pwa-app-info">
            <div className="pwa-app-title">Coupon Showcase App</div>
            <div className="pwa-app-subtitle">
              {isIOS
                ? 'Tippe auf Teilen ⎋ und "Zum Home-Bildschirm" ➕ für blitzschnellen Zugriff.'
                : 'Als App installieren für Sofort-Zugriff auf alle Gutscheine & Codes.'}
            </div>
          </div>
        </div>

        <div className="pwa-banner-actions">
          {!isIOS && deferredPrompt && (
            <Button
              variant="primary"
              size="sm"
              className="pwa-install-btn"
              onClick={handleInstallClick}
            >
              <HiDownload className="me-1" /> Installieren
            </Button>
          )}
          <button
            className="pwa-close-btn"
            onClick={handleDismiss}
            aria-label="Installationshinweis schließen"
          >
            <HiX />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default PWAInstallBanner;
